// The app's offline wiring on @dowty/offline: one IndexedDB database, a typed read cache per
// cached read, and the drink outbox. Database name and cache prefix are unchanged so queued
// drinks and caches written by earlier builds keep working.
import { AuthExpired, createOfflineDb, createOutbox, createReadCache } from '@dowty/offline';
import { getAuthCookie, isNetworkError } from '@dowty/core';
import { Drink, DrinkActivity } from '../generated/drinks_pb';
import type { ActiveEventResponse } from '../generated/drinks_pb';
import { UserData } from '../generated/user_pb';
import { drinksClient } from './drinksClient';
import { dateToTimestamp } from './timestamp';

const offlineDb = createOfflineDb('dowtex-offline');
const cacheOptions = { schemaVersion: 1 };

export const drinksCache = createReadCache<string, Drink.AsObject[]>(offlineDb, cacheOptions);
export const activeEventCache = createReadCache<string, ActiveEventResponse.AsObject | undefined>(offlineDb, cacheOptions);

/** Plain-JS fields addDrinkingEvent needs; the token is never stored, it is read when sending. */
export interface PendingDrinkPayload {
  drink: { drinkid: number; drinkname: string; promille: number };
  amount: number;
  /** Epoch ms, stamped when the drink was logged so a late sync keeps its place in the history. */
  tsInsert: number;
  eventId?: number;
  user: { userid: number; username: string };
}

function buildDrinkActivity({ drink, amount, tsInsert, eventId, user }: PendingDrinkPayload, token: string): DrinkActivity {
  const drinkMessage = new Drink();
  drinkMessage.setDrinkid(drink.drinkid);
  drinkMessage.setDrinkname(drink.drinkname);
  drinkMessage.setPromille(drink.promille);
  const activity = new DrinkActivity();
  activity.setDrink(drinkMessage);
  const userData = new UserData();
  userData.setUserid(user.userid);
  userData.setUsername(user.username);
  userData.setToken(token);
  activity.setUser(userData);
  activity.setAmount(amount);
  activity.setTsinsert(dateToTimestamp(new Date(tsInsert)));
  if (eventId !== undefined) activity.setEventid(eventId);
  return activity;
}

export const outbox = createOutbox<PendingDrinkPayload>(offlineDb, {
  send: async (payload, idempotencyKey) => {
    const currentUser = getAuthCookie();
    if (currentUser?.userId !== payload.user.userid) throw new AuthExpired();
    const token = currentUser?.token;
    if (!token) throw new AuthExpired();
    await drinksClient.addDrinkingEvent(buildDrinkActivity(payload, token), { 'idempotency-key': idempotencyKey });
  },
  isNetworkError,
  ordered: false,
  shouldSend: (payload) => payload.user.userid === getAuthCookie()?.userId,
});
